import {FormActionButtons, FormattedFormRow, FormContainer, FormRowDivider} from "@dvrt/graphql-widgets";
import {useBoolState} from "@/hooks";
import {Checkbox, MenuItem, Select, Typography, TextareaAutosize, IconButton} from "@mui/material";
import {useHookClass, TextField} from "@dvrt/graphql-widgets";
import DataSource from "@/components/domain_settings/settings/DataSource";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {FormattedPageTitle} from "@/components/pages";
import DomainSettingsForm from "@/components/domain_settings/settings/DomainSettingsForm";
import {defineMessage, FormattedMessage} from "react-intl";
import Loading from "@/components/breadcrumbs/Loading";
import {formPagesWidth} from "@/constants";
import ColorPicker from "@/components/ColorPicker";
import {EnumField, FieldWithToggle} from "@dvrt/graphql-widgets";
import OutgoingCallBarringWidget from "@/outgoing-call-barring/OutgoingCallBarringWidget";
import {Features, PrivateRoutingType} from "@/api";
import {ImageRow} from "@/components/domain_settings/settings/ImageRow";
import {AudioRow} from "@/components/domain_settings/settings/AudioRow";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {createInitialData} from "./initialData";
import {ReactNode, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Box} from "@mui/system";
import {PRIVATE_ROUTING_TYPE_MESSAGES} from "@/utils/translations";

export enum ImageType {
    LOGO = "logo",
    BACKGROUND = "background",
    LOGIN = "login",
}

type PasswordHide = {
    type: "text" | "password";
    icon: "eye" | "eye-slash";
};

type Props = {
    id: number;
};

const Content = ({dataSource, domainId}: {dataSource: DataSource; domainId: number}) => {
    const auth = useAuth();
    const domainContextFilter = ContextFilters.domain(dataSource.data.current.settings.domains.items[0].name);
    const canEditSuperuser = auth.hasPermission("edit_domain_settings_superuser", domainContextFilter);

    const form = new DomainSettingsForm(dataSource, canEditSuperuser);
    const [enabled, enable, disable] = useBoolState(true);

    const [passwordFieldProps, setPasswordFieldProps] = useState<PasswordHide>({
        type: "password",
        icon: "eye",
    });

    async function submit() {
        disable();

        if (await form.submit()) {
            const newData = await dataSource.fetch();
            form.newInitialData(createInitialData(newData));
        }

        enable();
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "domainSettings.pageTitle", defaultMessage: "Domain settings"})}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.description", defaultMessage: "Description"})}
                >
                    <TextField
                        value={form.values.description}
                        onChange={(value) => form.updateValues({description: value.toString()})}
                        disabled={!enabled || form.isRestrictedField("description")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.companyName", defaultMessage: "Company name"})}
                >
                    <TextField
                        value={form.values.companyName}
                        onChange={(value) => form.updateValues({companyName: value.toString()})}
                        disabled={!enabled || form.isRestrictedField("companyName")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.stationNames", defaultMessage: "Station names"})}
                >
                    <Select
                        value={form.values.preferDisplayNamesForStationNames ? 1 : 0}
                        onChange={(ev) =>
                            form.updateValues({preferDisplayNamesForStationNames: Boolean(ev.target.value)})
                        }
                        disabled={!enabled || form.isRestrictedField("preferDisplayNamesForStationNames")}
                        margin="dense"
                        size="small"
                        variant="outlined"
                        fullWidth={true}
                    >
                        <MenuItem key={0} value={0} id="domain-station-name-use-e164">
                            <Typography variant="subtitle1">
                                <FormattedMessage id="use_e164" defaultMessage="Use E.164 number" />
                            </Typography>
                        </MenuItem>
                        <MenuItem key={1} value={1} id="domain-station-name-use-display-name">
                            <Typography variant="subtitle1">
                                <FormattedMessage id="use_display_name" defaultMessage="Use display name" />
                            </Typography>
                        </MenuItem>
                    </Select>
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.smtpAuthEnabled",
                        defaultMessage: "Allow SMTP authentication",
                    })}
                >
                    <Checkbox
                        checked={form.values.smtp.enabled}
                        onChange={(_, enabled) => form.updateValues({smtp: {enabled}})}
                        disabled={!enabled || form.isRestrictedField("smtp.enabled")}
                    />
                </FormattedFormRow>
                {form.values.smtp.enabled && (
                    <>
                        <FormattedFormRow
                            label={defineMessage({id: "domainSettings.smtpUsername", defaultMessage: "SMTP username"})}
                        >
                            <TextField
                                value={form.values.smtp.username}
                                onChange={(username) => form.updateValues({smtp: {username}})}
                                disabled={!enabled || form.isRestrictedField("smtp.username")}
                            />
                        </FormattedFormRow>
                        <FormattedFormRow
                            label={defineMessage({id: "domainSettings.smtpUserpass", defaultMessage: "SMTP password"})}
                        >
                            <Box display="flex">
                                <TextField
                                    type={passwordFieldProps.type}
                                    value={form.values.smtp.userpass}
                                    onChange={(userpass) => form.updateValues({smtp: {userpass}})}
                                    disabled={!enabled || form.isRestrictedField("smtp.userpass")}
                                />
                                <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                        passwordFieldProps.type === "text"
                                            ? setPasswordFieldProps({type: "password", icon: "eye"})
                                            : setPasswordFieldProps({type: "text", icon: "eye-slash"})
                                    }
                                >
                                    <FontAwesomeIcon icon={passwordFieldProps.icon} style={{minWidth: 25}} />
                                </IconButton>
                            </Box>
                        </FormattedFormRow>
                    </>
                )}
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.pinBasedCalls", defaultMessage: "PIN based calls"})}
                >
                    <Checkbox
                        checked={form.values.pinBasedCalls}
                        onChange={() => form.updateValues({pinBasedCalls: !form.values.pinBasedCalls})}
                        disabled={!enabled || form.isRestrictedField("pinBasedCalls")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.presenceInDomainPreauthorized",
                        defaultMessage:
                            "User presence information is available inside the domain without authorization.",
                    })}
                >
                    <Checkbox
                        checked={form.values.presenceInDomainPreauthorized}
                        onChange={() =>
                            form.updateValues({
                                presenceInDomainPreauthorized: !form.values.presenceInDomainPreauthorized,
                            })
                        }
                        disabled={!enabled || form.isRestrictedField("presenceInDomainPreauthorized")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.privateRoutingType",
                        defaultMessage: "Custom routing type",
                    })}
                >
                    <EnumField
                        value={form.values.privateRoutingType}
                        enum_={{PrivateRoutingType}}
                        onChange={(val) =>
                            form.updateValues({
                                privateRoutingType: val as PrivateRoutingType,
                            })
                        }
                        disabled={!enabled || form.isRestrictedField("privateRoutingType")}
                        translations={PRIVATE_ROUTING_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.dialoutPrefix", defaultMessage: "Dialout prefix"})}
                >
                    <TextField
                        value={form.values.dialoutPrefix}
                        onChange={(value) => form.updateValues({dialoutPrefix: value})}
                        disabled={!enabled || form.isRestrictedField("dialoutPrefix")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.attendantPrefix", defaultMessage: "Attendant prefix"})}
                >
                    <TextField
                        value={form.values.attendantPrefix}
                        onChange={(value) => form.updateValues({attendantPrefix: value})}
                        disabled={!enabled || form.isRestrictedField("attendantPrefix")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.recordingRetention",
                        defaultMessage: "Recording retention (in days)",
                    })}
                >
                    <FieldWithToggle
                        checkBoxProps={{
                            disabled: !enabled || form.isRestrictedField("recording.retention"),
                        }}
                        fieldProps={{
                            variant: "outlined",
                            margin: "dense",
                            min: 1,
                            max: 3650,
                            style: {margin: 0},
                            disabled: !enabled || form.isRestrictedField("recording.retention"),
                        }}
                        fieldValue={form.values.recording.retention}
                        defaultFieldValue={31}
                        onChange={(retention) => form.updateValues({recording: {retention}})}
                        checkedValue={0}
                        checkBoxLabel={defineMessage({
                            id: "domainSettings.recordingRetentionCheckbox",
                            defaultMessage: "Use system default",
                        })}
                        tooltip={defineMessage({
                            id: "domainSettings.recordingRetentionTooltip",
                            defaultMessage:
                                "System default is set to be used. To change the value, uncheck the checkbox.",
                        })}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.requirePinForStartingPrivateCalls",
                        defaultMessage: "Require PIN for starting private calls",
                    })}
                >
                    <Checkbox
                        checked={form.values.requirePinForStartingPrivateCalls}
                        onChange={() =>
                            form.updateValues({
                                requirePinForStartingPrivateCalls: !form.values.requirePinForStartingPrivateCalls,
                            })
                        }
                        disabled={!enabled || form.isRestrictedField("attendantPrefix")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.groupDomain", defaultMessage: "Fax group domain"})}
                >
                    <TextField
                        value={form.values.groupDomain}
                        onChange={(value) => form.updateValues({groupDomain: value})}
                        disabled={!enabled || form.isRestrictedField("groupDomain")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "domainSettings.faxSignature", defaultMessage: "Fax signature"})}
                >
                    <TextareaAutosize
                        minRows={10}
                        maxRows={15}
                        value={form.values.faxSignature}
                        onChange={(ev) => form.updateValues({faxSignature: ev.target.value.toString()})}
                        style={{margin: 0, minWidth: "250px"}}
                        disabled={!enabled || form.isRestrictedField("faxSignature")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.recordingLocation",
                        defaultMessage: "Override recording location",
                    })}
                >
                    <TextField
                        value={form.values.recording.location || ""}
                        onChange={(location) => form.updateValues({recording: {location}})}
                        disabled={!enabled || form.isRestrictedField("recording.location")}
                        errors={form.errorHelper.fieldErrors("recording.location")}
                        infoText={defineMessage({
                            id: "domainSettings.recordingLocationTooltip",
                            defaultMessage:
                                "If set, recording is not stored internally but sent to the specified location. Allowed locations: mailto:<email address> or sftp:<user>@<host>:<path>",
                        })}
                    />
                </FormattedFormRow>
                <IfFeatureEnabled feature="domainBaseColor">
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.baseColor", defaultMessage: "Base color"})}
                    >
                        <ColorPicker
                            value={form.values.baseColor}
                            placeholder="Choose or enter a color"
                            onChange={(color) => form.updateValues({baseColor: color})}
                            disabled={!enabled || form.isRestrictedField("baseColor")}
                        />
                        {form.errorHelper.hasError("baseColor") && (
                            <Typography variant="subtitle2" style={{flex: 1, cursor: "default", color: "red"}}>
                                <FormattedMessage
                                    id="value_error.invalid_color"
                                    defaultMessage="Invalid color. Color must be a valid name or hex code. Example: red, #C10303."
                                />
                            </Typography>
                        )}
                    </FormattedFormRow>
                </IfFeatureEnabled>
                <IfFeatureEnabled feature="domainBackgroundImage">
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.backgroundColor",
                            defaultMessage: "Background color",
                        })}
                    >
                        <ColorPicker
                            value={form.values.background.color}
                            placeholder="Choose or enter a color"
                            onChange={(color) => form.updateValues({background: {color}})}
                            disabled={!enabled || form.isRestrictedField("background.color")}
                        />
                        {form.errorHelper.hasError("background.color") && (
                            <Typography variant="subtitle2" style={{flex: 1, cursor: "default", color: "red"}}>
                                <FormattedMessage
                                    id="value_error.invalid_color"
                                    defaultMessage="Invalid color. Color must be a valid name or hex code. Example: red, #C10303."
                                />
                            </Typography>
                        )}
                    </FormattedFormRow>
                </IfFeatureEnabled>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.loginBackgroundColor",
                        defaultMessage: "Login background color",
                    })}
                >
                    <ColorPicker
                        value={form.values.login.backgroundColor}
                        placeholder="Choose or enter a color"
                        onChange={(backgroundColor) => form.updateValues({login: {backgroundColor}})}
                        disabled={!enabled || form.isRestrictedField("login.backgroundColor")}
                    />
                    {form.errorHelper.hasError("login.backgroundColor") && (
                        <Typography variant="subtitle2" style={{flex: 1, cursor: "default", color: "red"}}>
                            <FormattedMessage
                                id="value_error.invalid_color"
                                defaultMessage="Invalid color. Color must be a valid name or hex code. Example: red, #C10303."
                            />
                        </Typography>
                    )}
                </FormattedFormRow>
                <IfFeatureEnabled feature="domainLoginImage">
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.loginLogoImage", defaultMessage: "Login logo image"})}
                    >
                        <ImageRow
                            image={{...form.values.login.logoImage, url: form.values.imageUrls.loginLogo}}
                            disabled={!enabled || form.isRestrictedField("login.logoImage")}
                            onChange={(logoImage) => form.updateValues({login: {logoImage}})}
                            errors={form.errorHelper.fieldErrors("login.logoImage")}
                        />
                    </FormattedFormRow>
                </IfFeatureEnabled>
                <IfFeatureEnabled feature="domainBackgroundImage">
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.backgroundImage",
                            defaultMessage: "Background image",
                        })}
                    >
                        <ImageRow
                            image={{...form.values.background.image, url: form.values.imageUrls.background}}
                            disabled={!enabled || form.isRestrictedField("background.image")}
                            onChange={(image) => form.updateValues({background: {image}})}
                            errors={form.errorHelper.fieldErrors("background.image")}
                        />
                    </FormattedFormRow>
                </IfFeatureEnabled>
                <FormattedFormRow
                    label={defineMessage({
                        id: "domainSettings.backgroundIsCenter",
                        defaultMessage: "Background image align to center",
                    })}
                >
                    <Checkbox
                        checked={form.values.background.isCentered}
                        onChange={(_, isCentered) => form.updateValues({background: {isCentered}})}
                        disabled={!enabled || form.isRestrictedField("background.isCentered")}
                    />
                </FormattedFormRow>
                <IfFeatureEnabled feature="domainLogo">
                    <FormattedFormRow label={defineMessage({id: "domainSettings.logo", defaultMessage: "Logo"})}>
                        <ImageRow
                            image={{...form.values.logo, url: form.values.imageUrls.logo}}
                            disabled={!enabled || form.isRestrictedField("logo")}
                            onChange={(image) => form.updateValues({logo: image})}
                            errors={form.errorHelper.fieldErrors("logo")}
                        />
                    </FormattedFormRow>
                </IfFeatureEnabled>
                <FormattedFormRow label={defineMessage({id: "domainSettings.mohSound", defaultMessage: "Moh sound"})}>
                    <AudioRow
                        audio={form.values.sounds.moh}
                        disabled={!enabled || form.isRestrictedField("sounds.moh")}
                        onChange={(moh) => form.updateValues({sounds: {moh}})}
                        domainId={domainId}
                        errors={form.errorHelper.fieldErrors("sounds.moh")}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "domainSettings.parkSound", defaultMessage: "Park sound"})}>
                    <AudioRow
                        audio={form.values.sounds.park}
                        disabled={!enabled || form.isRestrictedField("sounds.park")}
                        onChange={(park) => form.updateValues({sounds: {park}})}
                        domainId={domainId}
                        errors={form.errorHelper.fieldErrors("sounds.park")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                    labelAlign="top"
                >
                    <OutgoingCallBarringWidget
                        operatorBarrings={form.values.outgoingCallBarring.operator}
                        adminBarrings={form.values.outgoingCallBarring.admin}
                        updateOperatorBarrings={(operator) => form.updateValues({outgoingCallBarring: {operator}})}
                        updateAdminBarrings={(admin) => form.updateValues({outgoingCallBarring: {admin}})}
                        disabledAdmin={!enabled || form.isRestrictedField("outgoingCallBarring.admin")}
                        disabledOperator={!enabled || form.isRestrictedField("outgoingCallBarring.operator")}
                    />
                </FormattedFormRow>
                <FormRowDivider />
                <FormattedFormRow>
                    <div style={{margin: "1em 0"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                            enabled={form.hasChanged && enabled}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

const IfFeatureEnabled = ({feature, children}: {feature: keyof Features; children: ReactNode}) => {
    const structureConfig = useStructureConfig();
    return structureConfig.isEnabledFeature(feature) ? children : null;
};

const DomainSettingsPage = ({id}: Props) => {
    const dataSource = useHookClass(DataSource, id);

    if (!dataSource.loaded) return <Loading />;

    return <Content dataSource={dataSource} domainId={id} />;
};

export default DomainSettingsPage;
